<script lang="ts" setup>
import {computed, ref} from 'vue'
import {Head} from '@inertiajs/vue2'
import {useEndpoint} from '@/common/utils/fetch'

interface Product {
  id: string
  name: string
  price: number
  description: string
  features: string[]
}

interface ShippingData {
  address: string
  city: string
  state: string
  zip: string
}

interface UserData {
  username: string
  email: string
  institution: string
  shipping: ShippingData
}

interface Props {
  cwUser?: UserData
  token?: string
  returnUrl: string
  products: Record<string, Product>
  sandbox?: boolean
}

const props = defineProps<Props>()

const selectedProducts = ref<string[]>([])

// route() is being wonky, so not using it for the moment
// const checkoutUrl = typeof route === 'function' ? route('classwallet.checkout') : '/classwallet/checkout'
const checkoutUrl = computed(() => `/classwallet/checkout?token=${props.token}`)
const checkoutEndpoint = useEndpoint(checkoutUrl)

const effectiveCwUser = computed(() => props.cwUser ?? makeDummyCwUser())
const hasSelectedProducts = computed(() => selectedProducts.value.length > 0)
const checkoutDisabled = computed(() => !hasSelectedProducts.value)

const continueUrl = ref('')

// const orderTotal = computed(() => selectedProducts.value.reduce((total, productId) => total + props.products[productId].price, 0))

function makeDummyCwUser(): UserData | null {
  if (!props.sandbox) return null

  return {
    username: 'Sandbox User',
    email: 'sandbox@example.com',
    institution: 'Sandbox Institution',
    shipping: {
      address: '123 Sandbox St',
      city: 'Sandbox City',
      state: 'SB',
      zip: '12345'
    }
  }
}

function withRemovedProduct(selectedProducts: string[], productId: string) {
  return selectedProducts.filter(p => p !== productId)
}

function withAddedProduct(selectedProducts: string[], productId: string) {
  return [...withRemovedProduct(selectedProducts, productId), productId]
}

function addProduct(productId: string) {
  selectedProducts.value = withAddedProduct(selectedProducts.value, productId)
}

function removeProduct(productId: string) {
  selectedProducts.value = withRemovedProduct(selectedProducts.value, productId)
}

function isProductSelected(productId: string) {
  return selectedProducts.value.includes(productId)
}

async function proceedToCheckout() {
  if (!hasSelectedProducts.value) return

  const payload = {
    selectedProducts: selectedProducts.value,
    sandbox: props.sandbox ? '1' : undefined
  }

  const response = await checkoutEndpoint.post(payload).execute()
  const data = await response.json()
  console.log('Checkout response data:', data)
  const checkoutUrl = data.checkoutUrl

  if (!checkoutUrl) {
    console.error('No checkout URL returned from server')
    return
  }

  continueUrl.value = checkoutUrl
  setTimeout(() => window.location.href = checkoutUrl, 5000)
}
</script>

<template>
  <div>
    <Head title="ClassWallet Shop"/>

    <div class="shop-container d-flex flex-column">
      <div class="d-flex justify-content-center align-items-center gap-3 mb-4">
        <img src="/assets/images/classwallet-logo.jpg" alt="ClassWallet" class="classwallet-logo">
        <h1 class="banner">Subscribe with ClassWallet</h1>
      </div>

      <div class="d-flex flex-wrap">
        <div class="flex-grow-1 flex-basis-0 mr-md-4" style="min-width: 60%;">
          <div class="order-summary">
            <div class="order-summary-title">Product Options</div>

            <div v-if="effectiveCwUser">
              <div class="product-options">
                <div
                  v-for="product in products"
                  :key="product.id"
                  class="card product-card"
                  :class="{ 'selected': isProductSelected(product.id) }"
                >
                  <div class="card-header">
                    <div class="d-flex justify-content-between align-items-center">
                      <h3 class="mb-0">{{ product.name }}</h3>
                      <div class="product-price">${{ product.price }}</div>
                    </div>
                  </div>

                  <div class="card-body">
                    <p class="card-text">
                      {{ product.description }}
                    </p>

                    <ul class="product-features">
                      <li v-for="(feature, index) in product.features" :key="index">
                        {{ feature }}
                      </li>
                    </ul>
                  </div>

                  <div class="card-footer">
                    <div class="d-flex justify-content-center">
                      <button
                        v-if="!isProductSelected(product.id)"
                        class="btn btn-primary"
                        @click="addProduct(product.id)"
                      >
                        Add To Order
                      </button>
                      <button v-else class="btn btn-secondary" @click="removeProduct(product.id)">
                        Remove from Order
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="alert alert-warning">
              No ClassWallet account information found. Please start your checkout from ClassWallet.
            </div>
          </div>

          <div class="d-flex justify-content-center align-items-center gap-3 mb-4">
            <div v-if="continueUrl" class="alert alert-soft-success">
              <p>
                After completing checkout, you will receive an email with a link to login to your Lyrics2Learn account.
                If you don't see the email within a few minutes, please check your spam folder.
              </p>
              <p><a :href="continueUrl" class="btn btn-primary">Click to continue to ClassWallet now</a></p>
            </div>
            <button
              v-else-if="hasSelectedProducts"
              class="btn btn-primary checkout-button"
              :disabled="checkoutDisabled"
              @click="proceedToCheckout"
            >
              Proceed to Checkout
            </button>
            <button
              v-else
              disabled
              class="btn btn-outline-secondary"
            >
              Select one or more products to continue
            </button>
          </div>

          <div class="user-info">
            <div class="user-info-title">ClassWallet Account Information</div>

            <div v-if="effectiveCwUser">
              <div class="user-info-item">
                <span class="user-info-label">Name:</span>
                <span>{{ effectiveCwUser.username }}</span>
              </div>

              <div class="user-info-item">
                <span class="user-info-label">Email:</span>
                <span>{{ effectiveCwUser.email }}</span>
              </div>

              <div class="user-info-item">
                <span class="user-info-label">Institution:</span>
                <span>{{ effectiveCwUser.institution }}</span>
              </div>

              <div class="user-info-item">
                <span class="user-info-label">Address:</span>
                <span>{{ effectiveCwUser.shipping.address }}</span>
              </div>

              <div class="user-info-item">
                <span class="user-info-label">City:</span>
                <span>{{ effectiveCwUser.shipping.city }}, {{
                    effectiveCwUser.shipping.state
                  }} {{ effectiveCwUser.shipping.zip }}</span>
              </div>
            </div>
            <div v-else class="alert alert-warning">
              No ClassWallet account information found. Please start your checkout from ClassWallet.
              <p class="mt-3">
                <a :href="returnUrl" class="btn btn-outline-primary">Return to ClassWallet</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shop-container {
  max-width: 1000px;
  margin: 0 auto;
}

.order-summary {
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 20px;
  margin-bottom: 20px;
}

.order-summary-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.user-info {
  margin-bottom: 20px;
}

.user-info-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
}

.user-info-item {
  margin-bottom: 10px;
}

.user-info-label {
  font-weight: bold;
}

.checkout-button {
  padding: 10px 30px;
  font-size: 18px;
}

.classwallet-logo {
  max-height: 50px;
  margin-bottom: 20px;
}

.product-options {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}

.product-card {
  flex: 1 1 calc(50% - 20px);
  min-width: 300px;
  transition: all 0.3s ease;
  position: relative;
}

.product-card.selected {
  border-color: #377dff !important;
  box-shadow: 0 0 10px rgba(55, 125, 255, 0.3);
}

.product-card.selected .card-header {
  background-color: rgba(55, 125, 255, 0.1);
}

.product-card h3 {
  font-size: 18px;
  font-weight: bold;
}

.product-price {
  font-size: 20px;
  font-weight: bold;
  color: #007bff;
}

.product-features {
  padding-left: 20px;
  margin-bottom: 0;
  list-style-type: disc;
}

.product-features li {
  margin-bottom: 8px;
}
</style>
